import type {
  OptionalFaceKey,
  ProductColorOption,
  ProductMeasurementKind,
} from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { useStore } from 'zustand';
import { usePhotoDraftStore } from '@/entities/edit-area';
import { ColorFacePhotoGroup } from './color-face-photo-group';
import { CompositeAssetUpload } from './composite-asset-upload';
import { PhotoDraftLeaveGuard } from './photo-draft-leave-guard';
import { PhotoFaceOptions } from './photo-face-options';
import { PhotoReadiness } from './photo-readiness';
import { PhotoUploadNotice } from './photo-upload-notice';

interface FacePhotoEditorProps {
  readonly colors: readonly ProductColorOption[];
  readonly managed?: boolean;
  readonly disabled?: boolean;
  readonly onBeforeFaceRemoval?: (face: OptionalFaceKey) => boolean;
  /**
   * 상품 실측 종류. 원통 상품(CYLINDER)이면 면 공통 합성 에셋 줄을 보인다(Q8) — 전체 편집 store 가 있는
   * managed 화면에서만 뜬다. 넘기지 않으면 보이지 않는다.
   */
  readonly measurementKind?: ProductMeasurementKind;
}

export const FacePhotoEditor = ({
  colors,
  managed = false,
  disabled = false,
  onBeforeFaceRemoval,
  measurementKind,
}: FacePhotoEditorProps): ReactElement => {
  const store = usePhotoDraftStore();
  const hasUnsavedWork = useStore(
    store,
    (state) => state.dirty || Object.keys(state.operations).length > 0,
  );
  return (
    <fieldset disabled={disabled} className="min-w-0 space-y-5">
      <PhotoUploadNotice />
      {hasUnsavedWork && !managed ? <PhotoDraftLeaveGuard /> : null}
      {colors.length === 0 ? (
        <p>먼저 상품 색상을 등록해 주세요.</p>
      ) : (
        <>
          <PhotoFaceOptions onBeforeRemoval={onBeforeFaceRemoval} managed={managed} />
          {managed && measurementKind === 'CYLINDER' ? (
            <CompositeAssetUpload colors={colors} />
          ) : null}
          {colors.map((color) => (
            <ColorFacePhotoGroup key={color.id} color={color} />
          ))}
          <PhotoReadiness colors={colors} />
        </>
      )}
    </fieldset>
  );
};
